import React from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Select,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  UndoOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePayments } from '../hooks';
import { Payment, PaymentStatus, PaymentType, PaymentMethod } from '../../api/services/adminService';

const { Title, Text } = Typography;
const ORANGE = '#fe5a1d';

const METHOD_COLORS: Record<PaymentMethod, string> = {
  CARD: 'processing',
  CASH: 'success',
  BANK_TRANSFER: 'purple',
  CREDITS: 'warning',
};

const STATUS_COLORS: Record<PaymentStatus, string> = {
  PENDING: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
  REFUNDED: 'default',
};

const TYPE_COLORS: Record<PaymentType, string> = {
  LISTING_FEE: 'cyan',
  CREDIT_PURCHASE: 'processing',
  SUBSCRIPTION: 'purple',
};

const PaymentsManagement: React.FC = () => {
  const { t } = useTranslation();
  const {
    payments,
    stats,
    loading,
    statsLoading,
    pagination,
    filters,
    handleComplete,
    handleFail,
    handleRefund,
    handleDelete,
    handleFilterChange,
    handleTableChange,
    refetch,
  } = usePayments();

  const columns = [
    {
      title: t('admin.payments_mgmt.col_id'),
      dataIndex: 'id',
      key: 'id',
      render: (v: string) => (
        <Text type="secondary" style={{ fontSize: 12, fontFamily: 'monospace' }}>
          {v.slice(0, 8)}...
        </Text>
      ),
    },
    {
      title: t('admin.payments_mgmt.col_amount'),
      key: 'amount',
      render: (_: any, record: Payment) => (
        <Text strong style={{ color: ORANGE }}>
          {Number(record.amount).toFixed(2)} ALL {record.currency}
        </Text>
      ),
    },
    {
      title: t('admin.payments_mgmt.col_method'),
      dataIndex: 'method',
      key: 'method',
      render: (v: PaymentMethod) => (
        <Tag color={METHOD_COLORS[v] || 'default'}>{v?.replace('_', ' ')}</Tag>
      ),
    },
    {
      title: t('admin.payments_mgmt.col_status'),
      dataIndex: 'status',
      key: 'status',
      render: (v: PaymentStatus) => (
        <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>
      ),
    },
    {
      title: t('admin.payments_mgmt.col_type'),
      dataIndex: 'type',
      key: 'type',
      render: (v: PaymentType) => (
        <Tag color={TYPE_COLORS[v] || 'default'}>{v?.replace('_', ' ')}</Tag>
      ),
    },
    {
      title: t('admin.payments_mgmt.col_date'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => (
        <Text type="secondary">{new Date(v).toLocaleDateString()}</Text>
      ),
    },
    {
      title: t('admin.payments_mgmt.col_actions'),
      key: 'actions',
      render: (_: any, record: Payment) => (
        <Space>
          {record.status === 'PENDING' && (
            <>
              <Popconfirm
                title={t('admin.payments_mgmt.popconfirm_complete')}
                onConfirm={() => handleComplete(record.id)}
                okText={t('admin.payments_mgmt.ok_complete')}
                okButtonProps={{ style: { background: '#52c41a', borderColor: '#52c41a' } }}
              >
                <Button
                  type="text"
                  icon={<CheckCircleOutlined />}
                  style={{ color: '#52c41a' }}
                  title={t('admin.payments_mgmt.tooltip_complete')}
                />
              </Popconfirm>
              <Popconfirm
                title={t('admin.payments_mgmt.popconfirm_fail')}
                description={t('admin.payments_mgmt.desc_mark_failed')}
                onConfirm={() => handleFail(record.id)}
                okText={t('admin.payments_mgmt.ok_mark_failed')}
                okType="danger"
              >
                <Button
                  type="text"
                  danger
                  icon={<CloseCircleOutlined />}
                  title={t('admin.payments_mgmt.tooltip_fail')}
                />
              </Popconfirm>
            </>
          )}
          {record.status === 'COMPLETED' && (
            <Popconfirm
              title={t('admin.payments_mgmt.popconfirm_refund')}
              description={t('admin.payments_mgmt.desc_refund')}
              onConfirm={() => handleRefund(record.id)}
              okText={t('admin.payments_mgmt.ok_refund')}
              okType="danger"
            >
              <Button
                type="text"
                icon={<UndoOutlined />}
                style={{ color: '#faad14' }}
                title={t('admin.payments_mgmt.tooltip_refund')}
              />
            </Popconfirm>
          )}
          <Popconfirm
            title={t('admin.payments_mgmt.popconfirm_delete')}
            description={t('admin.payments_mgmt.desc_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('admin.payments_mgmt.ok_delete')}
            okType="danger"
          >
            <Button type="text" danger icon={<DeleteOutlined />} title={t('admin.payments_mgmt.tooltip_delete')} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={4} style={{ margin: 0, color: 'var(--admin-text-primary)' }}>{t('admin.payments_mgmt.title')}</Title>
          <Text type="secondary">{t('admin.payments_mgmt.subtitle')}</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={refetch} loading={loading}>
          {t('admin.payments_mgmt.btn_refresh')}
        </Button>
      </div>

      {/* Stats Row */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} className="admin-stat-card">
            <Statistic
              title={<div className="admin-stat-title">{t('admin.payments_mgmt.stat_total_revenue')}</div>}
              value={stats?.totalRevenue ?? 0}
              precision={2}
              prefix={<div className="admin-stat-icon-wrapper"><DollarOutlined /></div>}
              suffix=" ALL"
              valueStyle={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} className="admin-stat-card">
            <Statistic
              title={<div className="admin-stat-title">{t('admin.payments_mgmt.stat_total_payments')}</div>}
              value={stats?.totalPayments ?? 0}
              valueStyle={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} className="admin-stat-card">
            <Statistic
              title={<div className="admin-stat-title">{t('admin.payments_mgmt.stat_pending')}</div>}
              value={stats?.pendingPayments ?? 0}
              valueStyle={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}
              loading={statsLoading}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false} className="admin-stat-card">
            <Statistic
              title={<div className="admin-stat-title">{t('admin.payments_mgmt.stat_completed')}</div>}
              value={stats?.completedPayments ?? 0}
              valueStyle={{ color: 'var(--admin-text-primary)', fontWeight: 700 }}
              loading={statsLoading}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter Bar */}
      <Card bordered={false} className="admin-filter-card">
        <Space wrap>
          <Select
            placeholder={t('admin.payments_mgmt.ph_filter_status')}
            allowClear
            className="admin-filter-select"
            style={{ width: 160 }}
            value={filters.status}
            onChange={val => handleFilterChange({ ...filters, status: val as PaymentStatus })}
            options={[
              { label: t('admin.payments_mgmt.status_pending'), value: 'PENDING' },
              { label: t('admin.payments_mgmt.status_completed'), value: 'COMPLETED' },
              { label: t('admin.payments_mgmt.status_failed'), value: 'FAILED' },
              { label: t('admin.payments_mgmt.status_refunded'), value: 'REFUNDED' },
            ]}
          />
          <Select
            placeholder={t('admin.payments_mgmt.ph_filter_type')}
            allowClear
            className="admin-filter-select"
            style={{ width: 180 }}
            value={filters.type}
            onChange={val => handleFilterChange({ ...filters, type: val as PaymentType })}
            options={[
              { label: t('admin.payments_mgmt.type_listing_fee'), value: 'LISTING_FEE' },
              { label: t('admin.payments_mgmt.type_credit_purchase'), value: 'CREDIT_PURCHASE' },
              { label: t('admin.payments_mgmt.type_subscription'), value: 'SUBSCRIPTION' },
            ]}
          />
          <Select
            placeholder={t('admin.payments_mgmt.ph_filter_method')}
            allowClear
            className="admin-filter-select"
            style={{ width: 180 }}
            value={filters.method}
            onChange={val => handleFilterChange({ ...filters, method: val as PaymentMethod })}
            options={[
              { label: t('admin.payments_mgmt.method_card'), value: 'CARD' },
              { label: t('admin.payments_mgmt.method_cash'), value: 'CASH' },
              { label: t('admin.payments_mgmt.method_bank_transfer'), value: 'BANK_TRANSFER' },
              { label: t('admin.payments_mgmt.method_credits'), value: 'CREDITS' },
            ]}
          />
        </Space>
      </Card>

      {/* Table */}
      <Card
        title={<span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--admin-text-primary)' }}>{t('admin.payments_mgmt.table_title_all')}</span>}
        className="admin-table-card"
        bordered={false}
      >
        <Table
          className="admin-table"
          rowKey="id"
          loading={loading}
          dataSource={payments}
          columns={columns}
          locale={{ emptyText: t('admin.payments_mgmt.empty_text') }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) => t('admin.payments_mgmt.page_summary', { from: range[0], to: range[1], total }),
            onChange: (page, pageSize) => handleTableChange({ current: page, pageSize }),
          }}
        />
      </Card>
    </Space>
  );
};

export default PaymentsManagement;
